import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader, SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { PriceNoticeFailures } from "@/components/admin/PriceNoticeFailures";

const tl = (v) => (v == null ? "—" : `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v))} TL`);
const pct = (r) => (r == null ? "—" : `%${Math.round(Number(r) * 100)}`);
const STATUS_TR = { draft: "Taslak", notified: "Bildirildi (bekliyor)", active: "Yürürlükte", superseded: "Geçmiş", discarded: "Vazgeçildi" };
const PLAN_TR = { monthly: "Aylık", yearly: "Yıllık" };

function Line({ label, children, testId }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right" data-testid={testId}>{children}</span>
    </div>
  );
}

function NotifyDialog({ plan, open, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get(`/admin/pricing/${plan}/preview`).then(({ data }) => setPreview(data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Önizleme alınamadı"); onOpenChange(false); });
  }, [open, plan, onOpenChange]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/pricing/${plan}/notify`);
      toast.success(`Bildirim gönderildi: ${data.sent} başarılı, ${data.failed} başarısız`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bildirim gönderilemedi");
    } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl" data-testid={`pricing-notify-dialog-${plan}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{PLAN_TR[plan]} fiyat değişikliğini bildir</AlertDialogTitle>
          <AlertDialogDescription>
            {preview ? `${preview.affected_subscribers} aktif ${PLAN_TR[plan].toLowerCase()} aboneye gerçek e-posta gönderilecek. Yeni fiyat mevcut abonelere ${fmtDate(preview.effective_from)} ve sonrasındaki yenilemelerde uygulanır; yeni abonelikler hemen yeni fiyatı öder.` : "Yükleniyor..."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {preview && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid={`pricing-email-preview-${plan}`}>
            <div className="font-semibold mb-2">{preview.email.subject}</div>{preview.email.body}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`pricing-notify-cancel-${plan}`}>Vazgeç</AlertDialogCancel>
          <AlertDialogAction onClick={send} disabled={busy || !preview} data-testid={`pricing-notify-confirm-${plan}`}>
            {busy ? "Gönderiliyor..." : `Sadece ${PLAN_TR[plan].toLowerCase()} abonelere bildir`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PlanCard({ plan, d, onChanged }) {
  const [net, setNet] = useState("");
  const [busy, setBusy] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const a = d.active || {};
  const s = d.scheduled;
  const vat = Number(a.vat_rate || 0.2);
  const draftGross = net ? Math.round(Number(net) * (1 + vat) * 100) / 100 : null;
  const createDraft = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/pricing/${plan}/draft`, { net_price: Number(net) });
      toast.success("Yeni fiyat taslak olarak oluşturuldu");
      setNet("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Taslak oluşturulamadı");
    } finally { setBusy(false); }
  };
  return (
    <SectionCard title={`${PLAN_TR[plan].toUpperCase()} PLAN`}>
      <div className="p-5 space-y-4" data-testid={`pricing-card-${plan}`}>
        <div>
          <Line label="Aktif net fiyat" testId={`pricing-${plan}-net`}>{tl(a.net_price)}</Line>
          <Line label="KDV">{pct(a.vat_rate)}</Line>
          <Line label="Toplam (KDV dahil)" testId={`pricing-${plan}-gross`}>{tl(a.gross_price)}</Line>
          <Line label="Aktif abone sayısı" testId={`pricing-${plan}-subscribers`}>{d.active_subscribers}</Line>
          <Line label="Planlanmış yeni fiyat" testId={`pricing-${plan}-scheduled`}>{s ? `${tl(s.gross_price)} (${STATUS_TR[s.status]})` : "—"}</Line>
          <Line label="Bildirim tarihi">{s?.notification_sent_at ? fmtDateTime(s.notification_sent_at) : "—"}</Line>
          <Line label="Yürürlük tarihi" testId={`pricing-${plan}-effective`}>{s?.effective_from ? fmtDate(s.effective_from) : "—"}</Line>
          {s?.notify_stats && s.status === "notified" && (
            <Line label="Bildirilen / başarısız">{`${s.notify_stats.sent} / ${s.notify_stats.failed}`}</Line>
          )}
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-xs text-slate-500">Yeni net fiyat (TL)</label>
            <Input type="number" min="1" step="0.01" value={net} onChange={(e) => setNet(e.target.value)} data-testid={`pricing-${plan}-new-net-input`} />
            {draftGross && <div className="text-xs text-slate-500 mt-1" data-testid={`pricing-${plan}-new-gross`}>KDV dahil: {tl(draftGross)}</div>}
          </div>
          <Button variant="outline" disabled={busy || !(Number(net) > 0) || s?.status === "notified"} onClick={createDraft} data-testid={`pricing-${plan}-create-draft-btn`}>
            Yeni fiyat oluştur
          </Button>
        </div>
        <Button className="w-full" disabled={s?.status !== "draft"} onClick={() => setNotifyOpen(true)} data-testid={`pricing-${plan}-notify-btn`}>
          Fiyat değişikliğini bildir
        </Button>
        <NotifyDialog plan={plan} open={notifyOpen} onOpenChange={setNotifyOpen} onDone={onChanged} />
      </div>
    </SectionCard>
  );
}

function History({ data }) {
  const rows = ["monthly", "yearly"].flatMap((p) => data[p].history.map((r) => ({ ...r, plan: p })));
  return (
    <SectionCard title="Fiyat Geçmişi">
      <div className="overflow-x-auto" data-testid="pricing-history">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>{["Plan", "Net", "KDV", "Toplam", "Durum", "Oluşturan", "Oluşturma", "Bildirim", "Yürürlük", "Bildirilen / Başarısız"].map((h) => <th key={h} className="px-4 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`pricing-history-row-${r.id}`}>
                <td className="px-4 py-2">{PLAN_TR[r.plan]}</td>
                <td className="px-4 py-2">{tl(r.net_price)}</td>
                <td className="px-4 py-2">{pct(r.vat_rate)}</td>
                <td className="px-4 py-2">{tl(r.gross_price)}</td>
                <td className="px-4 py-2">{STATUS_TR[r.status] || r.status}</td>
                <td className="px-4 py-2">{r.created_by}</td>
                <td className="px-4 py-2 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                <td className="px-4 py-2 whitespace-nowrap">{r.notification_sent_at ? fmtDateTime(r.notification_sent_at) : "—"}</td>
                <td className="px-4 py-2 whitespace-nowrap">{r.effective_from && r.created_by !== "system_seed" ? fmtDate(r.effective_from) : "—"}</td>
                <td className="px-4 py-2">{r.notify_stats ? `${r.notify_stats.sent} / ${r.notify_stats.failed}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/pricing").then(({ data: d }) => setData(d)).catch(() => toast.error("Fiyatlar yüklenemedi"));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6" data-testid="admin-pricing">
      <PageHeader title="Abonelik Fiyatları" subtitle="Tek fiyat kaynağı — ödeme ekranı, ilk ödeme, aylık ve yıllık yenileme" />
      {data && (
        <>
          <div className="grid lg:grid-cols-2 gap-6">
            <PlanCard plan="monthly" d={data.monthly} onChanged={load} />
            <PlanCard plan="yearly" d={data.yearly} onChanged={load} />
          </div>
          <PriceNoticeFailures reloadKey={data} />
          <History data={data} />
        </>
      )}
    </div>
  );
}
